import React from "react";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import type { ChildReportCard, ParentChild } from "../../../types/parentFamily";
import { reportCardSummaryLine } from "../../../utils/reportCardCaps";
import { NoticeboardButton } from "../../shared";
import {
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
  MANAGEMENT_ACTION_ROW_SX,
} from "../../../styles/managementCardActions";
import { lightTheme } from "../../../styles/theme";
import type { FamilyDeleteTarget } from "./useFamilyModals";

interface ReportCardsSectionProps {
  child: ParentChild;
  onOpenAddReport: (childId: string) => void;
  onViewReport: (childId: string, report: ChildReportCard) => void;
  onEditReport: (childId: string, report: ChildReportCard) => void;
  onDeleteReport: (target: FamilyDeleteTarget) => void;
}

export const ReportCardsSection: React.FC<ReportCardsSectionProps> = ({
  child,
  onOpenAddReport,
  onViewReport,
  onEditReport,
  onDeleteReport,
}) => (
  <Stack spacing={1.5}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="subtitle2">Report cards</Typography>
      <NoticeboardButton
        variant="secondary"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onOpenAddReport(child.id)}
      >
        Add report card
      </NoticeboardButton>
    </Stack>
    {(child.reportCards || []).length === 0 ? (
      <Typography variant="body2" color="text.secondary">
        No report cards for this family member.
      </Typography>
    ) : (
      [...(child.reportCards || [])]
        .sort((a, b) =>
          (b.issuedDate || b.createdAt).localeCompare(a.issuedDate || a.createdAt)
        )
        .map((report) => (
          <Paper key={report.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
            >
              <Box
                sx={{ minWidth: 0, cursor: "pointer", flex: 1 }}
                onClick={() => onViewReport(child.id, report)}
              >
                <Typography variant="body1">{report.periodLabel}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {reportCardSummaryLine(report)}
                </Typography>
                {(report.teacherComment || report.notes) && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {report.teacherComment || report.notes}
                  </Typography>
                )}
              </Box>
              <Box sx={MANAGEMENT_ACTION_ROW_SX}>
                <IconButton
                  size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                  aria-label="View report card"
                  onClick={() => onViewReport(child.id, report)}
                  sx={{ color: lightTheme.colors.primary }}
                >
                  <VisibilityIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                </IconButton>
                <IconButton
                  size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                  aria-label="Edit report card"
                  onClick={() => onEditReport(child.id, report)}
                  sx={{ color: lightTheme.colors.primary }}
                >
                  <EditIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                </IconButton>
                <IconButton
                  size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                  aria-label="Delete report card"
                  onClick={() =>
                    onDeleteReport({
                      type: "report",
                      childId: child.id,
                      id: report.id,
                      label: report.periodLabel,
                    })
                  }
                  sx={{ color: lightTheme.colors.error }}
                >
                  <DeleteIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                </IconButton>
              </Box>
            </Stack>
          </Paper>
        ))
    )}
  </Stack>
);
